import { Suspense, useEffect, useMemo } from "react";
import { Redirect, Route, Switch } from "react-router-dom";
import { I18nextProvider } from "react-i18next";
import { Loading } from "@p81-common/ui";
import { getI18nInstance } from "@p81-common/shell-core-js";
import CheckTenant from "components/auth/CheckTenant/CheckTenant";
import { ErrorBoundary } from "components/common/ErrorBoundary/ErrorBoundary";
import * as paths from "./paths";
import * as urlGenerators from "./urlGenerators";
import {
  useDocumentTitle,
  useI18nInit,
  useRequest,
  useScript,
  useWhitelabel,
} from "hooks";

import {
  automaticLicenseFlow,
  cpDemoTenantIds,
  createKnowledgeBaseLinks,
  ROLE_NOT_SYNCED_ERROR,
  ROLE_NOT_SYNCED_ERROR_MESSAGE,
} from "@p81-common/shell-core-js";
import { lazyWithRetry } from "@p81-common/shell-core-js";
import { env } from "@p81-common/shell-core-js";
import { CheckpointRouter } from "./checkpoint";
import type { AppProps } from "types";
import { Roles } from "types";
import { isCheckpointDomain } from "@p81-common/shell-core-js";
import {
  partnerCheck,
  SubscriptionStatus,
  syncUserRole,
} from "tools/cp-middleware-adapter";
import { useDispatch, useSelector } from "react-redux";
import {
  requestFailure,
  setUserData,
} from "store/api/cpCheckTenantGet/cpCheckTenantGetActions";
import { getLogoutReason } from "tools/localStorage";
import usePollSubscriptionStatus from "./hooks/usePollSubscriptionStatus";

let Authorized = lazyWithRetry(
  () => import("components/auth/Authorized/Authorized")
);

let AppSwitch = lazyWithRetry(() => import("./AppSwitch"));

let Auth0Lock = lazyWithRetry(
  () => import(`components/auth/Auth0Lock/Auth0Lock`)
);
let ResetPassword = lazyWithRetry(() => import("./routes/ResetPassword"));
let Invitation = lazyWithRetry(() => import("./routes/Invitation"));

let SWSSOAuth = lazyWithRetry(
  () => import("components/sonicwall/SWSSOAuth/SWSSOAuth")
);

let WrongWorkspace = lazyWithRetry(() => import("./routes/WrongWorkspace"));
let FindWorkspace = lazyWithRetry(() => import("./routes/FindWorkspace"));
let Blocked = lazyWithRetry(() => import("./routes/Blocked"));

function onIntercomLoad() {
  window.Intercom("boot", { app_id: "rgt4wcyq" });
}

function initDelighted() {
  if (!window.delighted) {
    window.delighted = [];
  }
}

function Authorization(props?: AppProps) {
  const { appName = "HarmonySASE", sessionConfig } = props;
  let { name, general } = useWhitelabel();
  let isI18NReady = useI18nInit({ name, general });
  createKnowledgeBaseLinks(name, general.knowledgeBaseUrl);
  useDocumentTitle();
  const dispatch = useDispatch();
  const [{ loading: loadingPartnerCheck }, sendPartnerCheckReq] = useRequest(
    partnerCheck
  );
  const { userData } = useSelector((s: any) => s.cpCheckTenantGet);

  // App-specific role bucket: any assigned role — including READ_ONLY —
  // counts as "allowed". Backend authorization (CpAllowedRoles/
  // CpForbiddenRoles, hasAccess) is the actual authority on what a role
  // can do; this is just the login/routing gate.
  const isAllowedRole = (roles: { name: string }[]) => {
    return roles.length > 0;
  };

  const isAdminRole = (roles: { name: Roles }[]) => {
    return roles?.some((role) =>
      [Roles.ADMIN, Roles.PRIMARY_ADMIN].includes(role.name)
    );
  };

  // Global roles are org-wide, so this stays narrow: only global admins
  // and global READ_ONLY are allowed through — unlike the app-specific
  // bucket above, other global roles (e.g. USER_MANAGER) must not grant
  // access to an app they have no app-specific role for.
  const isReadOnlyRole = (roles: { name: Roles }[]) => {
    return roles?.some((role) => role.name === Roles.READ_ONLY);
  };

  const {
    isUserAppAdmin,
    isRoleAllowed,
    isGlobalAdmin,
    isGlobalReadOnly,
  } = useMemo(() => {
    if (!isCheckpointDomain) return {};
    let userAppAdmin, roleAllowed, globalAdmin, globalReadOnly;

    const userRole = sessionConfig?.user?.roles;
    const lowerCaseAppName = appName?.toLocaleLowerCase();
    const userApplicationRoles = userRole?.application[lowerCaseAppName];
    const globalRoles = userRole?.global;

    if (userApplicationRoles) {
      userAppAdmin = isAdminRole(userApplicationRoles);
      roleAllowed = isAllowedRole(userApplicationRoles);
    }

    globalAdmin = isAdminRole(globalRoles);
    globalReadOnly = isReadOnlyRole(globalRoles);

    return {
      isUserAppAdmin: userAppAdmin,
      isRoleAllowed: roleAllowed,
      isGlobalAdmin: globalAdmin,
      isGlobalReadOnly: globalReadOnly,
    };
  }, [appName, sessionConfig?.user?.roles]);

  const hasCPWriteAccess = useMemo(() => {
    return isUserAppAdmin || isGlobalAdmin; //if admin in any scope, has write access (can create tenant)
  }, [isUserAppAdmin, isGlobalAdmin]);

  const isCPAccessDenied = useMemo(() => {
    // Combine both buckets rather than short-circuiting on the app-specific
    // one — an app role bucket that exists but is empty must not mask a
    // valid global role (e.g. a global READ_ONLY with no app-specific role).
    return !(Boolean(isRoleAllowed) || isGlobalAdmin || isGlobalReadOnly);
  }, [isRoleAllowed, isGlobalAdmin, isGlobalReadOnly]);

  const isCpDemoTenant = cpDemoTenantIds.includes(
    sessionConfig?.user?.activeTenant
  );

  useEffect(() => {
    if (isCheckpointDomain && (!isCPAccessDenied || isCpDemoTenant)) {
      sendPartnerCheckReq()
        .then(async ({ data }) => {
          dispatch(setUserData(data));
          if (data.error?.includes(ROLE_NOT_SYNCED_ERROR)) {
            try {
              await syncUserRole();
            } catch (syncErr: any) {
              dispatch(requestFailure(syncErr?.message || ROLE_NOT_SYNCED_ERROR_MESSAGE));
              throw syncErr;
            }
          }
        })
        .catch((err) => {
          dispatch(requestFailure(err.message));
        });
    }
  }, [dispatch, isCPAccessDenied, isCpDemoTenant, sendPartnerCheckReq]);

  const subscriptionStatus = usePollSubscriptionStatus();

  useScript("https://widget.intercom.io/widget/rgt4wcyq", {
    preventLoad: name !== "perimeter81",
    onLoad: onIntercomLoad,
    onCleanup: () => window.Intercom("shutdown"),
  });

  useScript(
    `https://www.googletagmanager.com/gtm.js?id=${env.REACT_APP_GOOGLE_TAG_MANAGER_ID}`,
    {
      preventLoad: name !== "perimeter81",
    }
  );

  useScript(
    `https://d2yyd1h5u9mauk.cloudfront.net/integrations/web/v1/library/${env.REACT_APP_DELIGHTED_ID}/delighted.js`,
    {
      beforeLoad: initDelighted,
      preventLoad: name !== "sonicwall",
    }
  );

  if (!isI18NReady || loadingPartnerCheck) {
    return <Loading />;
  }

  function showCPPages() {
    const {
      tenantExists,
      userExists,
      chargebeeLicenseExists,
      checkpointLicenseExists,
    } = userData || {};

    if (!isCheckpointDomain) {
      return false;
    }

    if (chargebeeLicenseExists && userExists) {
      return false;
    }

    if (
      tenantExists &&
      userExists &&
      !checkpointLicenseExists &&
      !chargebeeLicenseExists
    ) {
      return false;
    }

    if (
      !tenantExists ||
      !userExists ||
      !checkpointLicenseExists ||
      (isCPAccessDenied && !isCpDemoTenant)
    ) {
      return true;
    }

    if (automaticLicenseFlow) {
      const subscriptionStatusPendingOrFail =
        subscriptionStatus.status !== undefined &&
        subscriptionStatus.status !== SubscriptionStatus.SUCCESS;
      return !chargebeeLicenseExists && subscriptionStatusPendingOrFail;
    }

    return false;
  }

  if (showCPPages()) {
    return (
      <CheckpointRouter
        appProps={props}
        isCPAccessDenied={isCPAccessDenied}
        hasCPWriteAccess={hasCPWriteAccess}
        isCpDemoTenant={isCpDemoTenant}
        subscriptionStatus={subscriptionStatus.status}
      />
    );
  }

  // Wrap the entire authorized tree in I18nextProvider using host's
  // initialized i18next instance. Per the MF react-i18next guide:
  // https://module-federation.io/practice/frameworks/react/i18n-react.html
  // — each app wraps its subtree in its own Provider. React Context
  // flows across federated boundaries, so host components that are
  // federated into the MFE (e.g. ExpireLicenseAttentionBox, billing
  // widgets) read from host's I18nextContext via the Provider above
  // them in the tree and translate correctly. Sharing i18next as a
  // MF singleton alone is not sufficient — @chkp/mwc-core's nested
  // react-i18next v12.3.1 clobbers the shared setI18n, but Provider
  // context takes priority over module-level state.
  return (
    <I18nextProvider i18n={getI18nInstance()}>
    <ErrorBoundary>
      <Switch>
        {name !== "perimeter81" && [
          <Route path={paths.BLOCKED}>
            <Suspense fallback={<Loading />}>
              <Blocked />
            </Suspense>
          </Route>,

          <Route path={paths.WRONG_WORKSPACE}>
            <Suspense fallback={<Loading />}>
              <WrongWorkspace
                findWorkspaceUrl={urlGenerators.findWorkspaceUrl}
              />
            </Suspense>
          </Route>,

          <Route path={paths.FIND_WORKSPACE}>
            <Suspense fallback={<Loading />}>
              <FindWorkspace />
            </Suspense>
          </Route>,
        ]}

        <CheckTenant wrongWorkspaceUrl={urlGenerators.wrongWorkspaceUrl}>
          <Switch>
            {!isCheckpointDomain && (
              <Route
                path={paths.SIGN_IN}
                render={(props) => {
                  let token = new URLSearchParams(props.location.search).get(
                    "authtoken"
                  );
                  if (token) {
                    return (
                      <SWSSOAuth
                        {...props}
                        token={token}
                        signInUrl={urlGenerators.signInUrl}
                        rootUrl={urlGenerators.rootUrl}
                      />
                    );
                  }
                  return (
                    <Suspense fallback={<Loading />}>
                      <Auth0Lock
                        logoutReason={getLogoutReason()}
                        rootUrl={urlGenerators.rootUrl}
                        signInUrl={urlGenerators.signInUrl}
                        resetPasswordUrl={urlGenerators.resetPasswordUrl}
                      />
                    </Suspense>
                  );
                }}
              />
            )}
            <Route path={paths.RESET_PASSWORD}>
              <Suspense fallback={<Loading />}>
                <ResetPassword />
              </Suspense>
            </Route>
            <Route path={paths.INVITATION}>
              <Suspense fallback={<Loading />}>
                <Invitation />
              </Suspense>
            </Route>
            <Route
              path={paths.ROOT}
              render={() => (
                <Suspense fallback={<Loading />}>
                  <Authorized signInUrlGenerator={urlGenerators.signInUrl}>
                    <AppSwitch />
                  </Authorized>
                </Suspense>
              )}
            />
            <Redirect to={urlGenerators.rootUrl()} />
          </Switch>
        </CheckTenant>
      </Switch>
    </ErrorBoundary>
    </I18nextProvider>
  );
}

export default Authorization;
